import type {
    PatientDetailDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    flattenSeries,
    imageContouredOn,
} from '~/containers/patient-list/model';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import type { OpenResolution } from './resolveOpenIntent';

/**
 * The datasets an open has to retrieve to put the resolved workflow on screen,
 * as selection keys — or undefined when the whole patient is what was asked
 * for.
 *
 * An open brings what the workflow needs and leaves the rest on the Connector,
 * where the Setup step's "not loaded" list can fetch it. That narrowing is
 * driven by an *intent*: a plain Open still means the patient, and a selection
 * the user made by hand is never quietly capped.
 *
 * Registration also takes the REG objects between the two frames it opens.
 * They are single instances, they are what the step exists to work on, and
 * fetching them later would mean the step opens unable to do its job.
 */
export function necessaryDatasetKeys(
    resolution: OpenResolution,
    detail: PatientDetailDto | undefined
): string[] | undefined {
    const series = flattenSeries(detail);
    const byId = new Map(
        series.filter((s) => s.id).map((s) => [s.id as string, s])
    );

    const image = resolution.imageId ? byId.get(resolution.imageId) : undefined;
    const secondary = resolution.secondaryImageId
        ? byId.get(resolution.secondaryImageId)
        : undefined;

    const structureSet = resolution.structureSetId
        ? byId.get(resolution.structureSetId)
        : undefined;

    const wanted = [
        image,
        secondary,
        structureSet,
        // A structure set without its image cannot be shown at all — the
        // viewer takes the image FROM the set, and a set whose image was left
        // on the Connector ends the open in an error naming a dataset the
        // user never asked for. A coherent resolution already names that
        // image, so this adds nothing; it is here so an incoherent one costs
        // a redundant retrieval rather than a broken session.
        structureSet ? imageContouredOn(series, structureSet) : undefined,
        ...registrationsBetween(series, image, secondary),
    ];

    const keys = [
        ...new Set(
            wanted
                .filter((s): s is SeriesDto => s !== undefined)
                .map((s) => seriesKey(s))
                .filter((key): key is string => key !== undefined)
        ),
    ];

    // Nothing addressable resolved — a patient with no images, say. Narrowing
    // to nothing would open nothing; the caller's own selection is the honest
    // fallback, and the empty-patient path already reports it.
    return keys.length > 0 ? keys : undefined;
}

/** REG objects mapping between the two images' frames of reference, either way. */
function registrationsBetween(
    series: readonly SeriesDto[],
    image: SeriesDto | undefined,
    secondary: SeriesDto | undefined
): SeriesDto[] {
    const frames = [image?.frameOfReferenceUid, secondary?.frameOfReferenceUid];
    if (frames.some((frame) => !frame)) {
        return [];
    }
    return series.filter(
        (s) =>
            s.datasetType === 'Registration' &&
            frames.includes(s.sourceFrameOfReferenceUid ?? undefined) &&
            frames.includes(s.targetFrameOfReferenceUid ?? undefined)
    );
}
